/**
 * ============================================================
 * FernQDesigns - NOWPayments Checkout
 * ============================================================
 *
 * Flow:
 *
 * Shopify Cart
 *      ↓
 * NOWPayments JS
 *      ↓
 * Shopify App Proxy
 *      ↓
 * /apps/nowpayments/create
 *      ↓
 * Cloudflare Worker
 *      ↓
 * /api/nowpayments/create
 *      ↓
 * NOWPayments API
 *      ↓
 * invoice_url
 *      ↓
 * NOWPayments checkout
 *
 * Worker:
 * https://fernqdesigns-nowpayments.fernqdesigns.workers.dev
 *
 * Shopify App Proxy:
 * /apps/nowpayments
 *
 * IMPORTANT:
 * - No NOWPayments API key is stored here.
 * - No Shopify secret is stored here.
 * - The browser does NOT call the Worker directly.
 * - The browser calls Shopify App Proxy.
 * ============================================================
 */

(function () {
  'use strict';

  /*
   * ------------------------------------------------------------
   * Prevent duplicate loading
   * ------------------------------------------------------------
   */

  if (window.__nowpaymentsCartLoaded) {
    console.log('[NOWPayments] Script already loaded.');
    return;
  }

  window.__nowpaymentsCartLoaded = true;

  /*
   * ------------------------------------------------------------
   * Configuration
   * ------------------------------------------------------------
   */

  var cfg = window.NOWPaymentsConfig || {};

  /*
   * IMPORTANT:
   *
   * Your Worker uses:
   *
   * /api/nowpayments/create
   *
   * Therefore Shopify App Proxy uses:
   *
   * /apps/nowpayments/create
   *
   * DO NOT use:
   *
   * https://fernqdesigns-nowpayments.fernqdesigns.workers.dev/...
   *
   * The browser should go through Shopify App Proxy.
   */

  var ENDPOINT =
    cfg.endpoint ||
    '/apps/nowpayments/create';

  var MODE =
    cfg.mode ||
    'disabled';

  var LOADING_LABEL =
    cfg.loadingLabel ||
    'Creating payment...';

  /*
   * ------------------------------------------------------------
   * Only activate in NOWPayments mode
   * ------------------------------------------------------------
   */

  if (MODE !== 'nowpayments') {
    console.log(
      '[NOWPayments] Disabled. Current mode:',
      MODE
    );
    return;
  }

  console.log(
    '[NOWPayments] Enabled.'
  );

  console.log(
    '[NOWPayments] App Proxy endpoint:',
    ENDPOINT
  );

  /*
   * ------------------------------------------------------------
   * Checkout selectors
   * ------------------------------------------------------------
   */

  var STANDARD_SELECTORS = [
    '#checkout',
    '#CartDrawer-Checkout',
    'button[name="checkout"]',
    'input[name="checkout"]',
    '.pp-checkout-button',
    '.cart__checkout-button',
    '[type="submit"][name="checkout"]'
  ].join(',');

  /*
   * Explicit NOWPayments button
   */

  var EXPLICIT_SELECTOR =
    '[data-nowpayments-checkout], #NOWPaymentsCheckout';

  /*
   * ------------------------------------------------------------
   * Runtime state
   * ------------------------------------------------------------
   */

  var busy = false;

  var activeButton = null;

  var originalHtml = '';

  /*
   * ------------------------------------------------------------
   * Find checkout trigger
   * ------------------------------------------------------------
   */

  function findTrigger(target) {

    if (
      !target ||
      typeof target.closest !== 'function'
    ) {
      return null;
    }

    /*
     * Explicit NOWPayments button.
     */

    var explicit =
      target.closest(
        EXPLICIT_SELECTOR
      );

    if (explicit) {
      return explicit;
    }

    /*
     * Standard Shopify checkout buttons.
     */

    return target.closest(
      STANDARD_SELECTORS
    );
  }

  /*
   * ------------------------------------------------------------
   * Error handling
   * ------------------------------------------------------------
   */

  function getErrorHost(button) {

    if (!button) {
      return document.body;
    }

    return (
      button.closest(
        '.cart__ctas, .nowpayments-cta, .cart-drawer__footer'
      ) || button
    );
  }

  function clearError(button) {

    var host =
      getErrorHost(button);

    if (!host) {
      return;
    }

    var old =
      host.parentNode &&
      host.parentNode.querySelector(
        '.nowpayments-error'
      );

    if (old) {
      old.remove();
    }
  }

  function showError(
    button,
    message
  ) {

    clearError(button);

    var error =
      document.createElement('p');

    error.className =
      'nowpayments-error';

    error.setAttribute(
      'role',
      'alert'
    );

    error.style.marginTop =
      '10px';

    error.style.padding =
      '10px';

    error.textContent =
      message ||
      'Unable to start crypto payment.';

    var host =
      getErrorHost(button);

    if (
      host &&
      host.parentNode
    ) {
      host.insertAdjacentElement(
        'afterend',
        error
      );
    } else {
      document.body.appendChild(
        error
      );
    }
  }

  /*
   * ------------------------------------------------------------
   * Loading state
   * ------------------------------------------------------------
   */

  function setBusy(
    button,
    state
  ) {

    if (state) {

      activeButton =
        button;

      originalHtml =
        button.innerHTML;

      if (
        'disabled' in button
      ) {
        button.disabled =
          true;
      }

      button.setAttribute(
        'aria-busy',
        'true'
      );

      button.textContent =
        LOADING_LABEL;

    } else if (
      activeButton
    ) {

      if (
        'disabled' in activeButton
      ) {
        activeButton.disabled =
          false;
      }

      activeButton.removeAttribute(
        'aria-busy'
      );

      activeButton.innerHTML =
        originalHtml;

      activeButton =
        null;
    }
  }

  /*
   * ------------------------------------------------------------
   * Validate NOWPayments invoice URL
   * ------------------------------------------------------------
   */

  function isSafeUrl(url) {

    try {

      var parsed =
        new URL(
          url,
          window.location.origin
        );

      if (
        parsed.protocol !== 'https:'
      ) {
        return false;
      }

      var hostname =
        parsed.hostname.toLowerCase();

      return (
        hostname === 'nowpayments.io' ||
        hostname.endsWith('.nowpayments.io')
      );

    } catch (error) {

      console.error(
        '[NOWPayments] Invalid URL:',
        error
      );

      return false;
    }
  }

  /*
   * ------------------------------------------------------------
   * Read Shopify cart
   * ------------------------------------------------------------
   */

  async function getCart() {

    var response =
      await fetch(
        '/cart.js',
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }
      );

    if (!response.ok) {

      throw new Error(
        'Could not read your cart. Please refresh and try again.'
      );
    }

    var cart =
      await response.json();

    if (
      !cart ||
      !cart.item_count
    ) {

      throw new Error(
        'Your cart is empty.'
      );
    }

    return cart;
  }

  /*
   * ------------------------------------------------------------
   * Get currency
   * ------------------------------------------------------------
   */

  function getCurrency(cart) {

    var currency =
      (
        window.Shopify &&
        window.Shopify.currency &&
        window.Shopify.currency.active
      ) ||
      cart.currency ||
      'USD';

    return String(currency)
      .toUpperCase();
  }

  /*
   * ------------------------------------------------------------
   * Create NOWPayments invoice
   * ------------------------------------------------------------
   */

  async function startCheckout(button) {

    if (busy) {
      return;
    }

    busy = true;

    clearError(button);

    setBusy(
      button,
      true
    );

    try {

      /*
       * --------------------------------------------------------
       * Read current Shopify cart
       * --------------------------------------------------------
       */

      var cart =
        await getCart();

      console.log(
        '[NOWPayments] Cart:',
        cart
      );

      /*
       * --------------------------------------------------------
       * Currency
       * --------------------------------------------------------
       */

      var currency =
        getCurrency(cart);

      /*
       * --------------------------------------------------------
       * Locale
       * --------------------------------------------------------
       */

      var locale =
        (
          window.Shopify &&
          window.Shopify.locale
        ) ||
        document.documentElement.lang ||
        'en';

      /*
       * --------------------------------------------------------
       * Shopify shop
       * --------------------------------------------------------
       */

      var shop =
        (
          window.Shopify &&
          window.Shopify.shop
        ) ||
        window.location.hostname;

      /*
       * --------------------------------------------------------
       * Request body
       * --------------------------------------------------------
       *
       * The Worker currently retrieves the authoritative cart
       * itself using the forwarded Shopify cookie.
       *
       * The cart is included for compatibility/debugging,
       * but the Worker should NOT trust the browser amount.
       * --------------------------------------------------------
       */

      var body = {
        cart: cart,
        currency: currency,
        locale: locale,
        source: 'shopify-theme',
        shop: shop
      };

      console.log(
        '[NOWPayments] Sending request to Shopify App Proxy:',
        ENDPOINT
      );

      console.log(
        '[NOWPayments] Request:',
        body
      );

      /*
       * --------------------------------------------------------
       * IMPORTANT
       * --------------------------------------------------------
       *
       * This is NOT a direct Worker request.
       *
       * Browser:
       *
       * /apps/nowpayments/create
       *
       * Shopify App Proxy forwards it to:
       *
       * /api/nowpayments/create
       *
       * Shopify handles the App Proxy request/signature.
       *
       * Therefore:
       *
       * credentials = same-origin
       * mode        = same-origin
       * --------------------------------------------------------
       */

      var response =
        await fetch(
          ENDPOINT,
          {
            method: 'POST',

            credentials:
              'same-origin',

            mode:
              'same-origin',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body:
              JSON.stringify(body)
          }
        );

      /*
       * --------------------------------------------------------
       * Parse response
       * --------------------------------------------------------
       */

      var data = null;

      try {

        data =
          await response.json();

      } catch (error) {

        console.error(
          '[NOWPayments] Invalid JSON response:',
          error
        );
      }

      console.log(
        '[NOWPayments] App Proxy response:',
        data
      );

      /*
       * --------------------------------------------------------
       * HTTP error
       * --------------------------------------------------------
       */

      if (!response.ok) {

        throw new Error(
          (
            data &&
            (
              data.error ||
              data.message
            )
          ) ||
          'Payment server returned an error.'
        );
      }

      /*
       * --------------------------------------------------------
       * Missing response
       * --------------------------------------------------------
       */

      if (!data) {

        throw new Error(
          'Payment server returned an invalid response.'
        );
      }

      /*
       * --------------------------------------------------------
       * Backend error
       * --------------------------------------------------------
       */

      if (
        data.success !== true
      ) {

        throw new Error(
          data.error ||
          data.message ||
          'NOWPayments payment creation failed.'
        );
      }

      /*
       * --------------------------------------------------------
       * Get invoice URL
       * --------------------------------------------------------
       */

      var invoiceUrl =
        data.invoice_url;

      if (!invoiceUrl) {

        throw new Error(
          'NOWPayments did not return an invoice URL.'
        );
      }

      /*
       * --------------------------------------------------------
       * Security validation
       * --------------------------------------------------------
       */

      if (
        !isSafeUrl(invoiceUrl)
      ) {

        throw new Error(
          'NOWPayments returned an invalid payment URL.'
        );
      }

      console.log(
        '[NOWPayments] Payment created successfully.'
      );

      console.log(
        '[NOWPayments] Order ID:',
        data.order_id
      );

      console.log(
        '[NOWPayments] Invoice URL:',
        invoiceUrl
      );

      /*
       * --------------------------------------------------------
       * Redirect to NOWPayments
       * --------------------------------------------------------
       */

      window.location.assign(
        invoiceUrl
      );

    } catch (error) {

      console.error(
        '[NOWPayments] Checkout error:',
        error
      );

      setBusy(
        button,
        false
      );

      showError(
        button,
        error.message
      );

      busy = false;
    }
  }

  /*
   * ------------------------------------------------------------
   * CLICK HANDLER
   * ------------------------------------------------------------
   *
   * Capture phase is important because Shopify/Tagada may
   * attach their own checkout click handlers.
   * ------------------------------------------------------------
   */

  document.addEventListener(
    'click',
    function (event) {

      /*
       * NOWPayments mode only.
       */

      if (
        MODE !== 'nowpayments'
      ) {
        return;
      }

      var trigger =
        findTrigger(
          event.target
        );

      if (!trigger) {
        return;
      }

      console.log(
        '[NOWPayments] Checkout button clicked.'
      );

      console.log(
        '[NOWPayments] Intercepting Shopify checkout.'
      );

      /*
       * Stop Shopify default checkout.
       */

      event.preventDefault();

      event.stopPropagation();

      event.stopImmediatePropagation();

      /*
       * Start NOWPayments.
       */

      startCheckout(
        trigger
      );

    },
    true
  );

  /*
   * ------------------------------------------------------------
   * FORM SUBMISSION HANDLER
   * ------------------------------------------------------------
   */

  document.addEventListener(
    'submit',
    function (event) {

      /*
       * NOWPayments mode only.
       */

      if (
        MODE !== 'nowpayments'
      ) {
        return;
      }

      var form =
        event.target;

      if (!form) {
        return;
      }

      /*
       * Form action.
       */

      var action =
        form.getAttribute(
          'action'
        ) || '';

      /*
       * Checkout button inside form.
       */

      var checkoutButton =
        form.querySelector(
          STANDARD_SELECTORS
        );

      /*
       * Is this a Shopify checkout form?
       */

      var isCheckoutForm =
        action.indexOf(
          '/checkout'
        ) !== -1;

      if (
        !checkoutButton &&
        !isCheckoutForm
      ) {
        return;
      }

      /*
       * If checkout button isn't directly inside form,
       * find a submit button.
       */

      if (!checkoutButton) {

        checkoutButton =
          form.querySelector(
            'button[type="submit"], input[type="submit"]'
          );
      }

      if (!checkoutButton) {
        return;
      }

      console.log(
        '[NOWPayments] Checkout form intercepted.'
      );

      /*
       * Stop Shopify checkout.
       */

      event.preventDefault();

      event.stopPropagation();

      event.stopImmediatePropagation();

      /*
       * Start NOWPayments.
       */

      startCheckout(
        checkoutButton
      );

    },
    true
  );

  /*
   * ------------------------------------------------------------
   * Browser back button / bfcache
   * ------------------------------------------------------------
   */

  window.addEventListener(
    'pageshow',
    function (event) {

      if (
        event.persisted &&
        busy
      ) {

        setBusy(
          null,
          false
        );

        busy = false;
      }
    }
  );

  /*
   * ------------------------------------------------------------
   * Ready
   * ------------------------------------------------------------
   */

  console.log(
    '[NOWPayments] Checkout handler is ready.'
  );

  console.log(
    '[NOWPayments] App Proxy:',
    ENDPOINT
  );

})();